import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Visibility as VisibilityIcon,
  VideoCall as VideoCallIcon,
  LocationOn as LocationOnIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { SelectChangeEvent } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import apiService from '../../services/api';
import { ContactRevealEvent } from '../../types';
import { formatDateTimeIst } from '../../utils/dateUtils';

const ContactRevealEventsPage: React.FC = () => {
  const [events, setEvents] = useState<ContactRevealEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    event_type: '',
    date_from: '',
    date_to: '',
  });
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 10,
    total: 0,
  });

  const fetchEvents = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const params: any = {
        per_page: pagination.pageSize,
        page: pagination.page + 1,
      };
      
      if (filters.event_type) params.event_type = filters.event_type;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;

      const response = await apiService.getContactRevealEvents(params);
      setEvents(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.pagination.total,
      }));
    } catch (err: any) {
      console.error('Error fetching events:', err);
      setError(err.response?.data?.message || 'Failed to load contact reveal events');
      setEvents([]);
    } finally {
      setIsLoading(false);
    }
  }, [filters, pagination.page, pagination.pageSize]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    setPagination(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [filters]);

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 70,
    },
    {
      field: 'event_type',
      headerName: 'Event Type',
      width: 200,
      renderCell: (params) => {
        const type = params.value as string;
        const labelMap: Record<string, string> = {
          contact_reveal: 'Contact Reveal',
          video_consultation_booking: 'Video Consultation',
          in_person_booking: 'In-Person Booking',
          phone_booking: 'Phone Booking',
        };

        const IconComponent =
          type === 'contact_reveal' ? VisibilityIcon :
          type === 'video_consultation_booking' ? VideoCallIcon :
          type === 'in_person_booking' ? LocationOnIcon :
          PhoneIcon;

        return (
          <Box display="flex" alignItems="center" gap={1}>
            <IconComponent color={type === 'contact_reveal' ? 'primary' : type === 'video_consultation_booking' ? 'secondary' : 'action'} />
            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
              {labelMap[type] || (type || '').replace(/_/g, ' ')}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'professional',
      headerName: 'Professional',
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.professional?.name || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'patient',
      headerName: 'Patient',
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.patient?.name || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'subscription',
      headerName: 'Subscription',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.subscription?.subscription_reference || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'lead',
      headerName: 'Lead Reference',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.lead?.lead_reference || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 180,
      renderCell: (params) => formatDateTimeIst(params.value),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Contact Reveal Events</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel id="event-type-label">Event Type</InputLabel>
                <Select
                  labelId="event-type-label"
                  id="event-type-select"
                  value={filters.event_type}
                  label="Event Type"
                  onChange={(e: SelectChangeEvent<string>) => setFilters(prev => ({ ...prev, event_type: e.target.value as string }))}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="contact_reveal">Contact Reveal</MenuItem>
                  <MenuItem value="video_consultation_booking">Video Consultation</MenuItem>
                  <MenuItem value="in_person_booking">In-Person Booking</MenuItem>
                  <MenuItem value="phone_booking">Phone Booking</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="From Date"
                type="date"
                value={filters.date_from}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="To Date"
                type="date"
                value={filters.date_to}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={events}
              columns={columns}
              paginationMode="server"
              paginationModel={{ page: pagination.page, pageSize: pagination.pageSize }}
              onPaginationModelChange={(model) => setPagination(prev => ({ ...prev, page: model.page, pageSize: model.pageSize }))}
              pageSizeOptions={[10, 25, 50, 100]}
              rowCount={pagination.total}
              disableRowSelectionOnClick
              autoHeight
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ContactRevealEventsPage;

